import Rule from '@/models/scenario/Rule.js'
import { rulesFactory, getBuiltRules } from '@/models/scenario/RulesFactory.js'
import Modules from '@/models/Modules.js'
import { CarbonSynthesis, LCASynthesis } from '@/models/carbon/Synthesis'

import _ from 'lodash'

const SCENARIO_DEFAULT_TIMELINE = 2030

export default class Scenario {
  constructor (
    id,
    name,
    timeline,
    description,
    uuid,
    rules,
    carbonSynthesis = null,
    lcaSynthesis = null,
    ghgi = null
  ) {
    this.id = id
    if (name) {
      this.name = name
    } else {
      this.name = null
    }
    if (timeline) {
      this.timeline = timeline
    } else {
      this.timeline = SCENARIO_DEFAULT_TIMELINE
    }
    this.description = description
    this.uuid = uuid
    this.setGHGI(ghgi)
    this.setRules(rules)
    this.carbonSynthesis = CarbonSynthesis.createFromObj(carbonSynthesis)
    this.lcaSynthesis = CarbonSynthesis.createFromObj(lcaSynthesis)
  }

  get year () {
    return this.__ref.year
  }

  get entity () {
    return this.__ref.entity
  }

  get reductionRate () {
    return (
      Math.round(
        (100 -
          (this.carbonSynthesis.intensity * 100) /
            this.__ref.carbonSynthesis.intensity) *
          10
      ) / 10
    )
  }

  getGHGIIntensity (module) {
    return this.__ref.emissions[Modules.getParentModule(module)]
  }

  setGHGI (ghgi, updateSynthesis = true) {
    if (ghgi !== null) {
      this.ghgi = _.cloneDeep(ghgi)
      this.__ref = _.cloneDeep(ghgi)
      if (updateSynthesis) {
        this.carbonSynthesis = _.cloneDeep(ghgi.carbonSynthesis)
        this.lcaSynthesis = _.cloneDeep(ghgi.lcaSynthesis)
      }
    } else {
      this.ghgi = null
      this.__ref = null
    }
  }

  reinitGHGI (module = null) {
    if (module !== null) {
      this.ghgi[Modules.getParentModule(module)] = _.cloneDeep(
        this.__ref[Modules.getParentModule(module)]
      )
    } else {
      this.ghgi = _.cloneDeep(this.__ref)
    }
  }

  addRule (rule) {
    let nrule = Scenario.createRuleFromObj(rule, this, true)
    nrule.setScenario(this)
    // check the rule has constrained position
    if (nrule.forceAfterRule) {
      let index = this.rules.findLastIndex(
        (obj) => obj.name === nrule.forceAfterRule
      )
      if (index === -1) {
        this.rules.unshift(nrule)
      } else {
        this.rules.splice(index + 1, 0, nrule)
      }
    } else {
      this.rules.unshift(nrule)
    }
  }

  ruleCanBeAdded (rule, settings) {
    rule.setScenario(this)
    let canBeAdded = false
    let sameRules = this.rules.filter((obj) => obj.name === rule.name)
    if (sameRules.length === 0) {
      // do not allow the rule if no emissions,
      // ReplacePlaneNational got an infinite loop if called from here
      // when accessing section.type
      if (rule.name !== 'ReplacePlaneNational') {
        canBeAdded = rule.getTargetIntensity(settings).intensity > 0
      } else {
        canBeAdded = this.getGHGIIntensity(rule.module).intensity.intensity > 0
      }
    } else if (rule.hasLevel2Parameters) {
      for (let key of Object.keys(rule.level2)) {
        let selectedValues = []
        // get selected values
        for (let r of sameRules) {
          selectedValues = selectedValues.concat(r.level2[key].value)
        }
        // check if 'all' has already been selected
        if (selectedValues.length > 0) {
          // if all elements have been selected
          if (rule.isHierarchical(key)) {
            let missingNodes = []
            for (let pvalue of rule.getLevel2Objects(key)) {
              if (!selectedValues.includes(pvalue.id)) {
                missingNodes.push(pvalue)
              }
            }
            // missing nodes, check leaves
            if (missingNodes.length !== 0) {
              let allLeavesValuesSelected = true
              for (let node of missingNodes) {
                allLeavesValuesSelected = true
                for (let leaf of node.children) {
                  if (!selectedValues.includes(leaf.id)) {
                    allLeavesValuesSelected = false
                    break
                  }
                }
                if (!allLeavesValuesSelected) {
                  break
                }
              }
              if (!allLeavesValuesSelected) {
                canBeAdded = true
              }
            }
          } else {
            if (
              selectedValues.length !== sameRules[0].getLevel2Values(key).length
            ) {
              canBeAdded = true
            }
          }
        }
      }
    }
    return canBeAdded
  }

  removeRule (rule) {
    let index = this.rules.findIndex((obj) => obj.id === rule.id)
    this.rules.splice(index, 1)
  }

  setRules (rules) {
    this.rules = rules.map((obj) => {
      let nrule = Scenario.createRuleFromObj(obj, this)
      nrule.setScenario(this)
      return nrule
    })
  }

  getData (module) {
    return this.__ref[Modules.getParentModule(module)]
  }

  compute (options, module = null) {
    if (this.ghgi) {
      this.reinitGHGI(module)
      // apply the TARGET_ELEMENT rules
      let allModules = Modules.getModules(true)
      if (module) {
        allModules = [module]
      }
      for (let cmodule of allModules) {
        for (let rule of this.rules.filter(
          (obj) =>
            Modules.getParentModule(obj.module) ===
              Modules.getParentModule(cmodule) &&
            obj.target === Rule.TARGET_ELEMENT
        )) {
          this.ghgi[Modules.getParentModule(cmodule)] = rule.compute(
            this.ghgi[Modules.getParentModule(cmodule)]
          )
        }
      }
      // compute the ghgi
      this.ghgi.compute(options, this.ghgi.year, [], module)
      /*
       * apply the TARGET_EMISSIONS rules
       * level1 and level2 provided to the main rule function are an array
       * of paramters of all the same rules, ie., with the same name.
       */
      for (let cmodule of allModules) {
        let moduleRules = this.rules.filter(
          (obj) =>
            Modules.getParentModule(obj.module) ===
              Modules.getParentModule(cmodule) &&
            obj.target === Rule.TARGET_EMISSIONS
        )
        for (let ruleName of moduleRules.map((obj) => obj.name)) {
          let allRules = this.rules.filter((obj) => obj.name === ruleName)
          this.ghgi.emissions[Modules.getParentModule(cmodule)].intensity =
            allRules[0].compute(
              this.ghgi.emissions[Modules.getParentModule(cmodule)].intensity,
              allRules.map((obj) => obj.level1),
              allRules.map((obj) => obj.level2),
              this.ghgi[Modules.getParentModule(cmodule)]
            )
        }
      }
      // Update synthesis
      this.carbonSynthesis = new CarbonSynthesis({
        heatings: this.ghgi.emissions?.buildings?.heatings?.intensity,
        electricity:
          this.ghgi.emissions?.buildings?.electricity?.intensity?.toCarbonIntensity(),
        refrigerants: this.ghgi?.emissions?.buildings?.refrigerants?.intensity,
        water: this.ghgi?.emissions?.buildings?.water?.intensity,
        construction: this.ghgi?.emissions?.buildings?.construction?.intensity,
        commutes: this.ghgi?.emissions?.commutes?.intensity,
        foods: this.ghgi?.emissions?.foods?.intensity,
        travels: this.ghgi?.emissions?.travels?.intensity,
        vehicles: this.ghgi?.emissions?.vehicles?.intensity,
        devices: this.ghgi?.emissions?.devices?.intensity,
        purchases: this.ghgi?.emissions?.purchases?.intensity,
        ractivities: this.ghgi?.emissions?.ractivities?.intensity
      })
      this.lcaSynthesis = new LCASynthesis({
        heatings: this.emissions?.buildings?.heatings?.impact.intensity,
        electricity: this.emissions?.buildings?.electricity?.impact.intensity,
        refrigerants:
          this?.emissions?.buildings?.refrigerants?.impact.intensity,
        water: this?.emissions?.buildings?.water?.impact.intensity,
        construction:
          this?.emissions?.buildings?.construction?.impact.intensity,
        commutes: this?.emissions?.commutes?.impact.intensity,
        foods: this?.emissions?.foods?.impact.intensity,
        travels: this?.emissions?.travels?.impact.intensity,
        vehicles: this?.emissions?.vehicles?.impact.intensity,
        devices: this?.emissions?.devices?.impact.intensity,
        purchases: this?.emissions?.purchases?.impact.intensity
      })
    }
  }

  toDatabase () {
    let rules = []
    for (let rule of this.rules) {
      rules.push(rule.toDatabase())
    }
    return {
      id: this.id,
      name: this.name,
      timeline: this.timeline,
      description: this.description,
      uuid: this.uuid,
      rules: rules,
      carbonSynthesis: this.carbonSynthesis,
      lcaSynthesis: this.lcaSynthesis
    }
  }

  static getRules (rules) {
    return getBuiltRules(rules)
  }

  static get DEFAULT_TIMELINE () {
    return SCENARIO_DEFAULT_TIMELINE
  }

  // Cannot be in Rule class
  static createRuleFromObj (rule, scenario, forceNullID = false) {
    let level2 = rule.level2
    if (level2 === null) {
      level2 = {}
    }
    let ruleID = rule.id
    if (forceNullID) {
      ruleID = null
    }
    const ClassRule = rulesFactory(rule.name)
    return new ClassRule(ruleID, scenario, rule.level1, level2)
  }

  static createFromObj (scenario) {
    let cscenario = new Scenario(
      scenario.id,
      scenario.name,
      scenario.timeline,
      scenario.description,
      scenario.uuid,
      scenario.rules,
      scenario.carbonSynthesis,
      scenario.lcaSynthesis,
      scenario.ghgi
    )
    return cscenario
  }
}
